import { Bot, User } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { api, formatDuration, formatRelative } from "../api";
import { Button, ErrorState, LoadingState, ProviderMark } from "../components";
import { comparisonLine, type PlacementModel, type PlacementsResponse, providerName } from "./loopPlacements";
import { readinessLine } from "./loopDependencies";

export type LoopPlacementsState = {
  data: PlacementsResponse | null;
  loading: boolean;
  error: string;
  refresh: () => void;
  update: (placementId: string, status: "active" | "retired") => Promise<void>;
};

/** Loads the placements and observed providers for one loop and exposes the retire / mark active updates. */
export function useLoopPlacements(organizationId: string | undefined, loopId: string): LoopPlacementsState {
  const [data, setData] = useState<PlacementsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (!organizationId || !loopId) return;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    void api<PlacementsResponse>(`/api/app/loops/${loopId}/placements`, { signal: controller.signal }, organizationId)
      .then((response) => setData(response))
      .catch((caught: unknown) => {
        if (caught instanceof DOMException && caught.name === "AbortError") return;
        setError(caught instanceof Error ? caught.message : "Placements are unavailable.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [organizationId, loopId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);

  const update = useCallback(async (placementId: string, status: "active" | "retired") => {
    const { placement } = await api<{ placement: PlacementModel }>(`/api/app/loops/${loopId}/placements/${placementId}`, { method: "PATCH", body: JSON.stringify({ status }) }, organizationId);
    setData((current) => {
      if (!current) return current;
      const placements = current.placements.map((existing) => (existing.id === placement.id ? placement : existing));
      return { ...current, placements, current: placements.filter((entry) => entry.status === "active") };
    });
  }, [organizationId, loopId]);

  return { data, loading, error, refresh, update };
}

function formatDay(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(value));
}

function formatAbsolute(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function creatorLabel(placement: PlacementModel): string {
  if (placement.createdBy.type === "system") return "observed from runs";
  return placement.createdBy.type === "agent" ? "planned by an agent" : "planned by a person";
}

function PlacementItem({ placement, readOnly, onUpdate }: { placement: PlacementModel; readOnly: boolean; onUpdate: (placementId: string, status: "active" | "retired") => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const name = providerName(placement.provider);
  const agentName = placement.agentName && placement.agentName.trim().toLowerCase() !== name.toLowerCase() ? placement.agentName : null;

  async function change(status: "active" | "retired") {
    setBusy(true);
    setError("");
    try {
      await onUpdate(placement.id, status);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The placement could not be updated.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className={`placement-item placement-item--${placement.status}`}>
      <ProviderMark provider={placement.provider} size={30} />
      <div className="placement-item__body">
        <header>
          <strong>{name}{agentName ? <span> · {agentName}</span> : null}</strong>
          <span className={`placement-chip placement-chip--${placement.status}`}><i aria-hidden="true" />{placement.status}</span>
        </header>
        {placement.environment ? <p>{placement.environment}</p> : null}
        <small>
          {placement.status === "planned" ? readinessLine(placement.readiness) ?? "waiting for its first run" : placement.startedAt ? <>since <time dateTime={placement.startedAt} title={formatAbsolute(placement.startedAt)}>{formatDay(placement.startedAt)}</time></> : "start not recorded"}
          {placement.endedAt ? <> · ended <time dateTime={placement.endedAt} title={formatAbsolute(placement.endedAt)}>{formatDay(placement.endedAt)}</time></> : null}
          {" · "}{placement.createdBy.type === "user" ? <User size={10} aria-hidden="true" /> : <Bot size={10} aria-hidden="true" />} {creatorLabel(placement)}
        </small>
        {placement.note ? <small className="placement-item__note">{placement.note}</small> : null}
        {error ? <small role="alert" className="placement-item__error">{error}</small> : null}
      </div>
      {!readOnly ? (
        <div className="placement-item__actions">
          {placement.status !== "retired" ? <Button variant="ghost" busy={busy} onClick={() => void change("retired")}>Retire</Button> : null}
          {placement.status !== "active" ? <Button variant="ghost" busy={busy} onClick={() => void change("active")}>Mark active</Button> : null}
        </div>
      ) : null}
    </li>
  );
}

/**
 * Performance by agent, under Recent runs on loop detail: one row per provider observed in the
 * window, the comparison sentence only when two providers both qualify, and the placement history.
 * `embedded` drops the panel chrome and head so the page's own section row names it.
 */
export function PerformanceByAgent({ state, readOnly, embedded = false }: { state: LoopPlacementsState; readOnly: boolean; embedded?: boolean }) {
  const { data, loading, error, refresh, update } = state;
  const days = data?.window.days ?? 90;
  const comparison = data ? comparisonLine(data.observed, days) : null;
  const Wrapper = embedded ? "div" : "section";

  return (
    <Wrapper className={embedded ? "loop-performance-panel loop-performance-panel--embedded" : "panel cockpit-panel loop-performance-panel"} aria-label={embedded ? undefined : "Performance by agent"}>
      {!embedded ? (
        <div className="panel__head">
          <div><p className="eyebrow">Last {days} days</p><h2>Performance by agent</h2></div>
          {data ? <span className="loop-notes-count">{data.placements.length} {data.placements.length === 1 ? "placement" : "placements"}</span> : null}
        </div>
      ) : null}
      {loading ? (
        <LoadingState>Reading placements…</LoadingState>
      ) : error || !data ? (
        <ErrorState action={<Button variant="secondary" onClick={refresh}>Try again</Button>}>Placements unavailable: {error || "no data"}</ErrorState>
      ) : (
        <>
          {data.observed.providers.length > 0 ? (
            <ol className="agent-performance" aria-label={`Runs by agent over the last ${days} days`}>
              {data.observed.providers.map((summary) => {
                const evaluable = summary.successRate != null;
                const coverage = summary.runs ? Math.round((summary.costReportedRuns / summary.runs) * 100) : 0;
                return (
                  <li key={summary.provider} className="agent-performance__row">
                    <div className="agent-performance__agent"><ProviderMark provider={summary.provider} size={30} /><strong>{providerName(summary.provider)}</strong></div>
                    <dl>
                      <div><dt>Runs</dt><dd>{summary.runs}</dd><small>{summary.resolved} resolved</small></div>
                      <div><dt>Reliability</dt><dd>{evaluable ? `${Math.round(summary.successRate! * 100)}%` : "—"}</dd><small>{evaluable ? `n = ${summary.resolved}` : "needs 3 resolved"}</small></div>
                      <div><dt>Median runtime</dt><dd>{formatDuration(summary.medianDurationMs)}</dd><small>{summary.medianDurationMs == null ? "no duration data" : "successful runs"}</small></div>
                      <div><dt>Reported cost</dt><dd>{summary.costReportedRuns ? `$${summary.costUsd.toFixed(2)}` : "—"}</dd><small>{summary.costReportedRuns ? `${coverage}% of runs supplied cost` : "not reported"}</small></div>
                      <div><dt>First · last run</dt><dd>{formatDay(summary.firstRunAt) === formatDay(summary.lastRunAt) ? formatDay(summary.firstRunAt) : `${formatDay(summary.firstRunAt)} → ${formatDay(summary.lastRunAt)}`}</dd><small>{summary.lastRunAt ? formatRelative(summary.lastRunAt) : "—"}</small></div>
                    </dl>
                  </li>
                );
              })}
            </ol>
          ) : (
            <div className="cockpit-quiet"><p>No runs in the last {days} days.</p><small>Performance by agent needs reported runs with a provider. Declared placements alone do not count.</small></div>
          )}
          {comparison ? <p className="loop-performance__compare">{comparison}</p> : data.observed.providers.length > 1 ? <p className="loop-performance__compare loop-performance__compare--withheld">No comparison yet: two agents need three resolved outcomes each.</p> : null}
          {data.observed.unattributedRuns > 0 ? <p className="cockpit-panel__note">{data.observed.unattributedRuns} {data.observed.unattributedRuns === 1 ? "run" : "runs"} in this window did not report a provider and {data.observed.unattributedRuns === 1 ? "is" : "are"} not attributed above.</p> : null}
          <div className="placement-history">
            <h3 className="eyebrow">Placement history</h3>
            {data.placements.length > 0 ? (
              <ol>{data.placements.map((placement) => <PlacementItem key={placement.id} placement={placement} readOnly={readOnly} onUpdate={update} />)}</ol>
            ) : (
              <p className="placement-history__empty">No placements yet. The first reported run records one automatically; Move to another agent plans the next.</p>
            )}
          </div>
        </>
      )}
    </Wrapper>
  );
}
